/*
 * Copyright (c) 2020. World Wide Generation Ltd
 */

import React, { useMemo } from 'react';
import SDGIcon from '../../../sdg-icon/sdg-icon';
import { getSDGShortTitle } from '../../../../constants/sdg-data';
import { getFrameworksOptions, getStandardsOptions } from '../../../../constants/standards-frameworks';
import { UserMin } from '../../../../constants/users';
import { naturalSort } from '../../../../utils';
import { SelectOption } from '../../../../types/select';
import { SurveySettings } from '../../../../types/survey';
import { useAppSelector } from '../../../../reducers';
import { QuestionStatuses } from '../../../../constants/status';
import { FeaturePermissions } from '../../../../services/permissions/FeaturePermissions';
import { MultipleSelect, Option } from '../../../multiple-select';
import { SDG_CODES } from '../../../../constants/utr';
import { Materiality } from '../../../../types/initiative';
import { Scope } from '../../../../model/surveyData';
import { useGetCustomTagsQuery } from '../../../../api/metric-groups';
import { noTagOption } from '../../utils/filters';
import { QUESTION } from '../../../../constants/terminology';

export type FilterSettings = Omit<SurveySettings, 'groupBy' | 'viewLayout'>;

const getQuestionStatusOptions = ({ canAccessVerification, canAccessAssurance }: { canAccessVerification: boolean, canAccessAssurance: boolean }
  ): Option[] => {

  const getOption = (name: string, icon: string, colour: string, faded: boolean = false) => {
    return (
      <>
        <i className={`fa ${icon} ${faded ? 'faded' : ''} ${colour} mr-1`} />
        {name}
      </>
    );
  }
  const getNANROption = (name: string, text: string) => {
    return (
      <div>
        <span className={'icon mr-1'}>{text}</span>
        {name}
      </div>
    );
  }
  const items = [
    {
      value: QuestionStatuses.Public,
      searchString: `Public ${QUESTION.CAPITALIZED_PLURAL}`,
      label: getOption(`Public ${QUESTION.CAPITALIZED_PLURAL}`, 'fa-solid fa-eye', 'text-chateauGrey')
    },
    {
      value: QuestionStatuses.Private,
      searchString: `Private ${QUESTION.CAPITALIZED_PLURAL}`,
      label: getOption(`Private ${QUESTION.CAPITALIZED_PLURAL}`, 'fa-solid fa-eye-low-vision', 'text-chateauGrey')
    },
    {
      value: QuestionStatuses.NA,
      searchString: `Not Applicable ${QUESTION.CAPITALIZED_PLURAL}`,
      label: getNANROption(`Not Applicable ${QUESTION.CAPITALIZED_PLURAL}`, 'N/A')
    },
    {
      value: QuestionStatuses.NR,
      searchString: `Not Reporting ${QUESTION.CAPITALIZED_PLURAL}`,
      label: getNANROption(`Not Reporting ${QUESTION.CAPITALIZED_PLURAL}`, 'N/R')
    },
    {
      value: QuestionStatuses.Created,
      searchString: `Unanswered ${QUESTION.CAPITALIZED_PLURAL}`,
      label: getOption(`Unanswered ${QUESTION.CAPITALIZED_PLURAL}`, 'fa-user-clock', 'text-muted')
    }
  ];

  if (canAccessVerification) {
    items.push({
      value: QuestionStatuses.Updated,
      searchString: `Updated ${QUESTION.CAPITALIZED_PLURAL}`,
      label: getOption(`Updated ${QUESTION.CAPITALIZED_PLURAL}`, 'fa-user-edit', 'text-primary')
    });
  }

  if (canAccessVerification) {
    items.push({
      value: QuestionStatuses.Rejected,
      searchString: `Rejected ${QUESTION.CAPITALIZED_PLURAL}`,
      label: getOption(`Rejected ${QUESTION.CAPITALIZED_PLURAL}`, 'fa-user-times', 'text-danger')
    });
  }

  items.push({
    value: QuestionStatuses.Verified,
    searchString: `Verified ${QUESTION.CAPITALIZED_PLURAL}`,
    label: getOption(`Verified ${QUESTION.CAPITALIZED_PLURAL}`, 'fa-user-check', 'text-success')
  });

  if (canAccessAssurance) {
    const notAssuredText = 'Not selected for Assurance';
    items.push({
      value: QuestionStatuses.NotAssured,
      searchString: notAssuredText,
      label: getOption(notAssuredText, 'fa-award', 'text-ThemeTextLight')
    });

    items.push({
      value: QuestionStatuses.SentToAssurer,
      searchString: 'Selected for Assurance',
      label: getOption('Selected for Assurance', 'fa-award', 'text-ThemeAccentDark')
    });

    items.push({
      value: QuestionStatuses.Disputed,
      searchString: 'Disputed Assurance',
      label: getOption('Disputed Assurance', 'fa-award', 'text-ThemeDangerMedium')
    });

    const assuredText = 'Assured';
    items.push({
      value: QuestionStatuses.Assured,
      searchString: assuredText,
      label: getOption(assuredText, 'fa-award', 'text-ThemeSuccessMedium')
    });

    items.push({
      value: QuestionStatuses.Restated,
      searchString: `Restated ${QUESTION.CAPITALIZED_PLURAL}`,
      label: getOption(`Restated ${QUESTION.CAPITALIZED_PLURAL}`, 'fa-rotate', 'text-ThemeSuccessMedium')
    })
  }

  return items;
}

const getDelegationStatusOptions = ({ users, canAccessVerification }:
  { users?: UserMin[], canAccessVerification: boolean }): Option[] => {
  const getOption = (typeName: string, icon: string, colour: string, faded: boolean = false) => {
    return (
      <div className='w-100'>
        {icon ? <i style={{ width: '18px' }} className={`fa ${icon} ${faded ? 'faded' : ''} ${colour} mr-1`} /> : null}
        {typeName}
      </div>
    );
  };
  const options: Option[] = [
    {
      value: 'user-a',
      label: getOption('Assigned to me', 'fa-user', 'text-chateauGrey', false),
    },
  ];

  if (canAccessVerification) {
    options.push(
      {
        label: 'Assigned',
        value: '',
        isDisabled: true,
      },
      {
        value: 'con-a',
        searchString: 'Contributor Assigned',
        label: getOption('Contributor', 'fa-user-edit', 'text-primary', false),
      },
      {
        value: 'ver-a',
        searchString: 'Verifier Assigned',
        label: getOption('Verifier', 'fa-user-check', 'text-success', false),
      },
      {
        label: 'Unassigned',
        value: '',
        isDisabled: true,
      },
      {
        value: 'con-u',
        searchString: 'Contributor Unassigned',
        label: getOption('Contributor', 'fa-user-edit', 'text-chateauGrey', true),
      },
      {
        value: 'ver-u',
        searchString: 'Verifier Unassigned',
        label: getOption('Verifier', 'fa-user-check', 'text-chateauGrey', true),
      }
    );
  }

  if (users) {
    options.push(
      {
        label: 'Users',
        value: '',
        isDisabled: true,
      },
      ...users
        .filter((u) => u._id)
        .sort((a, b) => naturalSort(`${a.firstName} ${a.surname}`, `${b.firstName} ${b.surname}`))
        .map((u) => ({
          value: u._id,
          searchString: `${u.firstName} ${u.surname}`,
          label: getOption(`${u.firstName} ${u.surname}`, '', ''),
        }))
    );
  }

  return options;
};
const getMaterialityOptions = (): Option[] => {
  return [
    {
      value: Materiality.High,
      searchString: 'High',
      label: <span><i
        className='fa fa-tachometer-alt text-danger mr-1' />High</span>
    },
    {
      value: Materiality.Medium,
      searchString: 'Medium',
      label: <span><i
        className='fa fa-tachometer-alt text-warning mr-1' />Medium</span>
    },
    {
      value: Materiality.Low,
      searchString: 'Low',
      label: <span><i
        className='fa fa-tachometer-alt text-success mr-1' />Low</span>
    },
  ];
}
export const getSDGOptions = (): Option[] => {
  const getOption = (sdgGoal: number, text: string) => {
    return (
      <span>
        <SDGIcon height='26px' code={sdgGoal} className='mr-1' />
        <span>{text}</span>
      </span>
    );
  }
  return SDG_CODES.map((sdgGoal) => {
    const text = `Goal ${sdgGoal} - ${getSDGShortTitle(sdgGoal)}`;
    return {
      value: String(sdgGoal),
      searchString: text,
      label: getOption(sdgGoal, text),
    };
  });
}

export const onChangeHandlerSelectWrapper = (
  key: keyof SurveySettings,
  handleChangeSettings: (key: keyof SurveySettings, settings: string[]) => void
) => {
  return (selectedOption: SelectOption | null | Option['value'][]) => {
    if (Array.isArray(selectedOption)) {
      handleChangeSettings(key, selectedOption);
    } else if (!selectedOption || selectedOption.value === null) {
      handleChangeSettings(key, []);
    } else {
      handleChangeSettings(key, [selectedOption.value])
    }
  }
};

interface FilterQuestionsProps {
  initiativeId: string;
  settings: Partial<SurveySettings>;
  handleChangeSettings: (key: keyof SurveySettings, setting: string[]) => void;
  isAggregate?: boolean;
  surveyScope?: Scope;
}

export const FilterQuestions = (props: FilterQuestionsProps) => {
  const { initiativeId = '', settings, handleChangeSettings, isAggregate = false, surveyScope } = props;
  const canAccessAssurance = useAppSelector(FeaturePermissions.canAccessAssurance);
  const canAccessVerification = useAppSelector(FeaturePermissions.canAccessVerification);

  const users: UserMin[] = useAppSelector((state) => state.surveyDelegationUsers.data.users ?? []);
  const { data: tags = [] } = useGetCustomTagsQuery(initiativeId, { skip: !initiativeId });

  const {
    filterByStatus,
    filterByDelegationStatus,
    filterByFramework,
    filterByGoal,
    filterByMateriality,
    filterByStandard,
    filterByTag,
  } = settings;

  const questionStatusOptions = getQuestionStatusOptions({ canAccessAssurance, canAccessVerification });
  const delegationStatusOptions = getDelegationStatusOptions({ users, canAccessVerification });
  const materialityOptions = getMaterialityOptions();
  const sdgOptions = getSDGOptions();
  const standardsOptions = getStandardsOptions(surveyScope);
  const frameworksOptions = getFrameworksOptions(surveyScope);
  const tagOptions = useMemo(
    () => {
      const options = [noTagOption];
      tags.forEach((tag) => {
        options.push({ value: tag._id, searchString: tag.groupName, label: tag.groupName })
      })
      return options;
    },
    [tags]
  );

  const onChange = (key: keyof FilterSettings) => onChangeHandlerSelectWrapper(key, handleChangeSettings);

  return (
    <div className='row mx-0 filter-questions'>
      {!isAggregate && Array.isArray(filterByStatus) ? (
        <div className='col p-0'>
          <MultipleSelect
            placeholder='Status'
            options={questionStatusOptions}
            onChange={onChange('filterByStatus')}
            values={filterByStatus}
          />
        </div>
      ) : null}

      {!isAggregate && Array.isArray(filterByDelegationStatus) ? (
        <div className='col p-0'>
          <MultipleSelect
            placeholder='Delegation'
            options={delegationStatusOptions}
            onChange={onChange('filterByDelegationStatus')}
            values={filterByDelegationStatus}
          />
        </div>
      ) : null}

      {Array.isArray(filterByMateriality) ? (
        <div className='col p-0'>
          <MultipleSelect
            placeholder='Materiality'
            options={materialityOptions}
            onChange={onChange('filterByMateriality')}
            values={filterByMateriality}
          />
        </div>
      ) : null}

      {Array.isArray(filterByGoal) ? (
        <div className='col p-0'>
          <MultipleSelect
            placeholder='SDGs'
            options={sdgOptions}
            onChange={onChange('filterByGoal')}
            values={filterByGoal}
          />
        </div>
      ) : null}

      {Array.isArray(filterByStandard) ? (
        <div className='col p-0'>
          <MultipleSelect
            placeholder='Standards'
            options={standardsOptions}
            onChange={onChange('filterByStandard')}
            values={filterByStandard}
          />
        </div>
      ) : null}

      {Array.isArray(filterByFramework) ? (
        <div className='col p-0'>
          <MultipleSelect
            placeholder='Frameworks'
            options={frameworksOptions}
            onChange={onChange('filterByFramework')}
            values={filterByFramework}
          />
        </div>
      ) : null}

      {Array.isArray(filterByTag) ? (
        <div className='col p-0'>
          <MultipleSelect
            placeholder='Tags'
            options={tagOptions}
            onChange={onChange('filterByTag')}
            values={filterByTag}
          />
        </div>
      ) : null}
    </div>
  );
}
